import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { Interviewee } from '@smooved/core';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SurveyNavigation } from '../constants/surveys.constants';
import { SurveyType } from '../enums';
import { InterviewInitiatedByType } from '../enums/interview-initiated-by-type.enum';
import { Answers } from '../interfaces/data/answers';
import { Interview } from '../interfaces/meta/interview';
import { SelectionOption } from '../interfaces/meta/questions/selection-option';
import { SurveyWizardService } from '../services/survey-wizard.service';
import {
    getInterview as surveysGetInterview,
    patchAnswers,
    resetAnswers,
    setInitiatedBy,
    SurveyActionTypes,
} from '../state/surveys.actions';
import { getAnswers, getInterviewState, getSurveyState, SurveyState } from '../state/surveys.reducer';
import { SurveyProvidersModule } from '../survey-providers.module';

@Injectable({
    providedIn: SurveyProvidersModule,
})
export class SurveysSandbox {
    public survey$ = this.store$.pipe(select(getSurveyState));
    public interview$ = this.store$.pipe(select(getInterviewState));
    public interviewOnce$ = this.interview$.pipe(take(1));

    constructor(
        private readonly store$: Store<SurveyState>,
        private readonly action$: ActionsSubject,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly surveyWizardService: SurveyWizardService,
    ) {}

    public getInterview(
        interviewee: Interviewee,
        initiatedBy: InterviewInitiatedByType,
        callback?: (interview: Interview) => any
    ): Observable<Interview> {
        this.store$.dispatch(surveysGetInterview({ interviewee, initiatedBy, callback }));
        return this.action$.pipe(
            ofType(SurveyActionTypes.GetInterviewSuccess),
            switchMap(() => this.interviewOnce$)
        );
    }

    public getInterviewByType(
        interviewee: Interviewee,
        surveyType: SurveyType,
        callback?: (interview: Interview) => any
    ): Observable<Interview> {
        this.store$.dispatch(surveysGetInterview({ interviewee, surveyType, callback }));
        return this.action$.pipe(
            ofType(SurveyActionTypes.GetInterviewSuccess),
            switchMap(() => this.interviewOnce$)
        );
    }

    public hasChildren(options: SelectionOption[]): boolean {
        return options?.some((option): boolean => !!option.children);
    }

    public setInitiatedBy(initiatedBy: InterviewInitiatedByType): void {
        this.store$.dispatch(setInitiatedBy({ initiatedBy }));
    }

    public patch(answers: Answers): void {
        this.store$.dispatch(patchAnswers({ answers }));
    }

    public reset(): void {
        this.surveyWizardService.reset();
        this.store$.dispatch(resetAnswers());
    }

    public getAnswers(): Observable<Answers> {
        return this.store$.select(getAnswers);
    }

    public goToStart(): void {
        void this.router.navigate(['../..', SurveyNavigation.Start], {
            relativeTo: this.route,
        });
    }
}
